import i18n from '@/utils/i18n';
import React, { useEffect, useState } from 'react';
import { Chip } from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import {
  DEFAULT_CURRENCY_CODE,
  displayLineItemPrice,
  getPackPriceDisplay,
} from '@/utils/money';
import { FONTS } from '@/styles';
import { InboundDetailCorrectionPreview } from './inbound-detail-correction-preview';
import { InboundDetailListItemAction } from './inbound-detail-list-item-action';
import { InboundDetailListItemCorrectionAction } from './inbound-detail-list-item-correction-action';
import { InboundDetailOrderInfo } from './inbound-detail-order-info';
import { InboundDetailSalesRepInfo } from './inbound-detail-sales-rep-info';
import { InboundListItem, OrderAgentWorkflowMode } from './types';
import {
  ItemListOption,
  ItemListOptionMetadata,
} from '@/contexts/item-context-types';
import { MultiStyleText } from '@/components/multi-style-text';
import { OrderableItemLabels } from '@/components/orders/orderable-item-labels';
import {
  OrderableItemListItemImage,
  STANDARD_IMAGE_SIZE,
} from '@/components/orders/orderable-item-list-item-image';
import { QuantitySelector } from '@/components/quantity-selector';
import { TextInput } from '@/components/text-input';
import { Typography } from '@usepepper/ui/atoms/typography';
import {
  useAddToOrderDisabled,
  useAvailabilityStatusEnabled,
} from '@/hooks/use-add-to-order-disabled';
import { useBaseItemQueryVariables } from '@/hooks/use-item-query-variables';
import { useCanWriteLineItemRequest } from '@/hooks/dynamic-config/use-can-write-line-item-request';
import { useDebouncedCallback } from 'use-debounce';
import { useItemLabels, useRenderedLabels } from '@/hooks/use-item-labels';
import { useMaybePaymentsContext } from '@/contexts/payments-context';
import { useOrderInboundDetailStore } from './store/order-inbound-detail-store';
import { useOrderInbounds } from '@/hooks/use-order-inbounds';
import {
  useSupplierDynamicConfigFlag,
  useSupplierDynamicConfigValue,
} from '@/hooks/use-dynamic-config-value';
import { useToasts } from '@/contexts/toast-context';
import { useUpdateInboundItemNotes, useUpdateInboundQuantity } from './queries';
import { VariantPackWithQuantities } from '@/hooks/use-order-inbound-reducer';
import { View } from 'react-native';

const DEBOUNCE_DELAY_MS = 1500;
const ITEM_NOTE_DEBOUNCE_DELAY_MS = 300;
const OUT_OF_STOCK_OPACITY = 0.6;

interface Props {
  correctionPreview?: ItemListOptionMetadata | null;
  inboundListItem: InboundListItem;
  inDraggableSheet?: boolean;
  isHovered?: boolean;
  isOrderOpen: boolean;
  navigateToCorrection: (item: InboundListItem) => void;
  navigateToSearch: (item: InboundListItem) => void;
  onChange: (previousQuantity: number, newQuantity: number) => void;
  option: ItemListOption;
  restaurantUUID: string;
  smartOrderInputUUID: string | null;
  updateItemNotes: (searchInputUUID: string, itemNotes: string | null) => void;
  variantPackWithQuantities: VariantPackWithQuantities;
  workflowMode: OrderAgentWorkflowMode;
}

export const InboundDetailOrderItem: React.FC<Props> = ({
  correctionPreview,
  inboundListItem,
  inDraggableSheet,
  isHovered,
  navigateToCorrection,
  isOrderOpen,
  navigateToSearch,
  onChange,
  option,
  restaurantUUID,
  smartOrderInputUUID,
  updateItemNotes,
  variantPackWithQuantities,
  workflowMode,
}) => {
  const [styles, theme] = useStyles();
  const { showErrorToast } = useToasts();
  const { isViewOnly } = useOrderInbounds();
  const isCorrectionMode = workflowMode === OrderAgentWorkflowMode.Correction;
  const baseItemQueryVariables = useBaseItemQueryVariables();
  const [updateQuantityMutation] = useUpdateInboundQuantity();
  const paymentsContext = useMaybePaymentsContext();
  const metadata = option.metadata!;
  const quantity = variantPackWithQuantities.quantity;
  const isAddToOrderDisabled = useAddToOrderDisabled(metadata.variantPackUUID);
  const availabilityStatusEnabled = useAvailabilityStatusEnabled();
  const currencyCode =
    metadata.currencyCode ||
    paymentsContext?.currencyCode ||
    DEFAULT_CURRENCY_CODE;
  const itemLabels = useItemLabels(metadata);
  const renderedLabels = useRenderedLabels(itemLabels);

  const lineItemNotesConfig = useSupplierDynamicConfigValue(
    'line_item_notes_config',
  );
  const isSurfaceNotesEnabled = useSupplierDynamicConfigFlag(
    'smart_order_config',
    'surface_notes_enabled',
  );
  const canWriteLineItemRequest = useCanWriteLineItemRequest();
  const [updateItemNotesMutation] = useUpdateInboundItemNotes();
  const showItemNote =
    Boolean(lineItemNotesConfig?.enabled) && isSurfaceNotesEnabled;
  const isItemNoteEditable =
    !isCorrectionMode &&
    !isViewOnly &&
    canWriteLineItemRequest &&
    Boolean(smartOrderInputUUID);

  const setPendingItemNote = useOrderInboundDetailStore(
    (state) => state.actions.setPendingItemNote,
  );
  const clearPendingItemNote = useOrderInboundDetailStore(
    (state) => state.actions.clearPendingItemNote,
  );

  const committedItemNote =
    inboundListItem.metadata?.inputWithResult.itemNotes ?? '';
  const [itemNote, setItemNote] = useState(committedItemNote);

  // take inputUUID as a parameter to ensure flush is called with the correct inputUUID
  const debouncedCommitItemNote = useDebouncedCallback(
    async (nextItemNote: string, inputUUID: string) => {
      const previousItemNote = committedItemNote;
      if (nextItemNote === previousItemNote) {
        clearPendingItemNote(inputUUID);
        return;
      }
      updateItemNotes(inputUUID, nextItemNote || null);
      try {
        await updateItemNotesMutation({
          variables: {
            ...baseItemQueryVariables,
            itemNotes: nextItemNote || null,
            restaurantUUID,
            skipGroupMemberships: true,
            smartOrderInputUUID: inputUUID,
          },
        });
      } catch {
        showErrorToast();
        setItemNote(previousItemNote);
        updateItemNotes(inputUUID, previousItemNote || null);
      } finally {
        clearPendingItemNote(inputUUID);
      }
    },
    ITEM_NOTE_DEBOUNCE_DELAY_MS,
  );

  useEffect(() => {
    return () => {
      if (smartOrderInputUUID) {
        debouncedCommitItemNote.flush();
      }
    };
  }, [smartOrderInputUUID, debouncedCommitItemNote]);

  const onItemNoteChange = (nextItemNote: string) => {
    const maxLength = lineItemNotesConfig?.max_length;
    if (
      !smartOrderInputUUID ||
      (maxLength && nextItemNote.length > maxLength)
    ) {
      return;
    }
    setItemNote(nextItemNote);
    setPendingItemNote(smartOrderInputUUID, nextItemNote);
    debouncedCommitItemNote(nextItemNote, smartOrderInputUUID);
  };

  const debouncedUpdateInboundQuantity = useDebouncedCallback(
    async (nextQuantity: number, inputUUID: string) => {
      try {
        await updateQuantityMutation({
          variables: {
            ...baseItemQueryVariables,
            quantity: nextQuantity,
            restaurantUUID,
            skipGroupMemberships: true,
            smartOrderInputUUID: inputUUID,
          },
        });
      } catch {
        showErrorToast();
      }
    },
    DEBOUNCE_DELAY_MS,
  );

  useEffect(() => {
    // only run on dismount
    return () => {
      if (smartOrderInputUUID) {
        debouncedUpdateInboundQuantity.flush();
      }
    };
  }, [smartOrderInputUUID, debouncedUpdateInboundQuantity]);

  const extendedPrice = displayLineItemPrice({
    currencyCode,
    quantity,
    unitCount: metadata.unitCount,
    unitPriceInMicros:
      metadata.promotionPricePerUnitInMicros ?? metadata.unitPriceInMicros,
  });
  const {
    formattedPackPrice,
    formattedPromotionPackPrice,
    isPackPriceDiscounted,
    perUnitPriceCopy,
    perUnitPriceHasEmphasis,
  } = getPackPriceDisplay({
    baseUnit: metadata.baseUnit,
    currencyCode,
    promotionPricePerUnitInMicros: metadata.promotionPricePerUnitInMicros,
    unitCount: metadata.unitCount,
    unitPriceInMicros: metadata.unitPriceInMicros,
  });

  return (
    <View
      style={[styles.container, isHovered ? styles.containerHovered : null]}
    >
      <OrderableItemLabels option={option} />

      <View style={styles.headerRow}>
        {isCorrectionMode ? (
          <View style={styles.quantityBadge}>
            <Chip
              alignSelf="center"
              backgroundColor="$bgTertiaryDefault"
              borderColor="transparent"
              circular
              size="$md"
            >
              <Chip.Text>{quantity}</Chip.Text>
            </Chip>
          </View>
        ) : (
          <OrderableItemListItemImage
            alt={metadata.displayName}
            hidePlaceholderImages={false}
            imageSize={STANDARD_IMAGE_SIZE}
            imageURLs={metadata.photoURLs}
            style={styles.image}
          />
        )}

        <View style={styles.titleColumn}>
          <Typography numberOfLines={2} variant="paragraph-md-600">
            {option.title}
          </Typography>

          {metadata.description ? (
            <Typography
              color={theme.sk.color.text.secondary}
              numberOfLines={2}
              variant="paragraph-sm"
            >
              {metadata.description}
            </Typography>
          ) : null}
        </View>

        {isHovered ? (
          isCorrectionMode && !isViewOnly ? (
            <InboundDetailListItemCorrectionAction
              inboundListItem={inboundListItem}
              navigateToCorrection={navigateToCorrection}
            />
          ) : (
            <InboundDetailListItemAction
              inboundListItem={inboundListItem}
              navigateToSearch={navigateToSearch}
            />
          )
        ) : (
          <View style={styles.priceStatusRow}>
            <Typography variant="paragraph-md-600">{extendedPrice}</Typography>
            {renderedLabels}
          </View>
        )}
      </View>

      <View style={styles.detailBox}>
        <View style={styles.priceRow}>
          <View style={styles.skuPriceColumn}>
            <Typography
              color={theme.sk.color.text.secondary}
              variant="paragraph-sm"
            >
              <Typography textTransform="capitalize" variant="paragraph-sm">
                {metadata.packDisplayString}
              </Typography>
              {metadata.externalID ? (
                <Typography textTransform="uppercase" variant="paragraph-sm">
                  {` • ${metadata.externalID}`}
                </Typography>
              ) : null}
            </Typography>

            <View style={styles.priceLine}>
              {formattedPromotionPackPrice ? (
                <Typography variant="paragraph-sm">
                  {formattedPromotionPackPrice}
                </Typography>
              ) : null}

              {formattedPackPrice ? (
                <Typography
                  textDecorationLine={
                    isPackPriceDiscounted ? 'line-through' : undefined
                  }
                  variant="paragraph-sm"
                >
                  {formattedPackPrice}
                </Typography>
              ) : null}

              {perUnitPriceCopy ? (
                perUnitPriceHasEmphasis ? (
                  <MultiStyleText
                    defaultStyle={styles.perUnitText}
                    emphasisStyle={styles.perUnitStrikethrough}
                  >
                    {perUnitPriceCopy}
                  </MultiStyleText>
                ) : (
                  <Typography
                    color={theme.sk.color.text.secondary}
                    textTransform="lowercase"
                    variant="paragraph-sm"
                  >
                    {perUnitPriceCopy}
                  </Typography>
                )
              ) : null}
            </View>
          </View>

          {isCorrectionMode ? null : (
            <>
              {isAddToOrderDisabled ? (
                availabilityStatusEnabled ? null : (
                  <View style={styles.unavailable}>
                    <Typography variant="paragraph-md-600">
                      {i18n.t('unavailable')}
                    </Typography>
                  </View>
                )
              ) : (
                <QuantitySelector
                  disabled={isViewOnly}
                  inDraggableSheet={inDraggableSheet}
                  isFocused={false}
                  itemDisplayName={metadata.displayName}
                  maxQuantity={metadata.maximumQuantity}
                  minQuantity={metadata.minimumQuantity}
                  onChange={(previousQuantity, newQuantity) => {
                    onChange(previousQuantity, newQuantity);
                    // deletions handled outside of this component
                    if (smartOrderInputUUID && newQuantity !== 0) {
                      debouncedUpdateInboundQuantity(
                        newQuantity,
                        smartOrderInputUUID,
                      );
                    }
                  }}
                  quantity={quantity}
                  showTooltipOnMount
                />
              )}
            </>
          )}
        </View>

        <InboundDetailOrderInfo variantPackMetadata={metadata} />
      </View>

      {showItemNote ? (
        <TextInput
          containerStyle={styles.itemNoteInput}
          disabledStyle={styles.itemNoteInputDisabled}
          editable={isItemNoteEditable}
          inDraggableSheet={inDraggableSheet}
          onChangeText={onItemNoteChange}
          placeholder={i18n.t('order_guide_note.request_placeholder')}
          style={styles.itemNoteInputText}
          testID="inbound-detail-item-note-input"
          value={itemNote}
        />
      ) : null}

      {isOrderOpen ? (
        <InboundDetailSalesRepInfo
          inDraggableSheet={inDraggableSheet}
          itemMetadata={metadata}
        />
      ) : null}

      {correctionPreview && isCorrectionMode ? (
        <InboundDetailCorrectionPreview
          previewItem={correctionPreview}
          searchTerm={inboundListItem.metadata?.matchTerm ?? option.title}
        />
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    gap: 8,
    padding: 16,
  },
  containerHovered: {
    backgroundColor: theme.sk.color.bg.secondary.default,
  },
  detailBox: {
    alignSelf: 'stretch',
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    gap: 4,
  },
  headerRow: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    flexDirection: 'row',
    gap: 8,
  },
  image: {
    borderRadius: theme.sk.radii.radiusMedium,
  },
  itemNoteInput: {
    alignSelf: 'stretch',
    borderColor: theme.sk.color.border.input.default,
    borderRadius: theme.sk.radii.radiusMedium,
    height: 40,
  },
  itemNoteInputDisabled: {
    color: theme.sk.color.text.primary,
  },
  itemNoteInputText: {
    fontFamily: theme.sk.typography.input.md.fontFamily,
    fontSize: theme.sk.typography.input.md.fontSize,
    letterSpacing: theme.sk.typography.input.md.letterSpacing,
    paddingHorizontal: theme.sk.spacing.spaceSMedium,
  },
  perUnitStrikethrough: {
    ...FONTS.primary,
    color: theme.sk.color.text.secondary,
    fontSize: theme.sk.typography.paragraph.sm.fontSize,
    lineHeight: theme.sk.typography.paragraph.sm.lineHeight,
    textDecorationLine: 'line-through',
    textTransform: 'lowercase',
  },
  perUnitText: {
    ...FONTS.primary,
    color: theme.sk.color.text.secondary,
    fontSize: theme.sk.typography.paragraph.sm.fontSize,
    lineHeight: theme.sk.typography.paragraph.sm.lineHeight,
    textTransform: 'lowercase',
  },
  priceLine: {
    alignItems: 'center',
    columnGap: 8,
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  priceRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: theme.sk.spacing.spaceSMedium,
  },
  priceStatusRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  quantityBadge: {
    alignItems: 'center',
    alignSelf: 'stretch',
    justifyContent: 'center',
    width: STANDARD_IMAGE_SIZE,
  },
  skuPriceColumn: {
    alignItems: 'flex-start',
    gap: 4,
  },
  titleColumn: {
    alignItems: 'flex-start',
    flex: 1,
    gap: 2,
  },
  unavailable: {
    justifyContent: 'center',
    opacity: OUT_OF_STOCK_OPACITY,
  },
}));
